import type { Translations } from './ar';

export const en: Translations = {
  dir: 'ltr',
  lang: 'en',

  common: {
    appName: 'Syria 14',
    tagline: 'Unlock Your Perfect Stay',
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    filter: 'Filter',
    clear: 'Clear',
    apply: 'Apply',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    or: 'or',
    and: 'and',
    all: 'All',
    noResults: 'No results found',
    required: 'Required',
    optional: 'Optional',
    viewAll: 'View All',
    savedToFavorites: 'Saved to favorites',
    removedFromFavorites: 'Removed from favorites',
    skipToContent: 'Skip to main content',
  },

  nav: {
    home: 'Home',
    properties: 'Properties',
    offices: 'Offices',
    search: 'Search',
    contact: 'Contact',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    dashboard: 'Dashboard',
    account: 'My Account',
    favorites: 'Favorites',
    adminPanel: 'Admin Panel',
  },

  auth: {
    login: 'Login',
    register: 'Create Account',
    logout: 'Logout',
    email: 'Email Address',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    fullName: 'Full Name',
    phone: 'Phone Number',
    forgotPassword: 'Forgot your password?',
    resetPassword: 'Reset Password',
    rememberMe: 'Remember me',
    loginWithGoogle: 'Continue with Google',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    signUpHere: 'Sign up here',
    signInHere: 'Sign in here',
    passwordMinLength: 'Password must be at least 8 characters',
    passwordsNotMatch: 'Passwords do not match',
    invalidEmail: 'Invalid email address',
    loginSuccess: 'Logged in successfully',
    registerSuccess: 'Account created successfully',
    logoutSuccess: 'Logged out successfully',
    sendResetLink: 'Send Reset Link',
    checkEmail: 'Check your email for a reset link',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    profile_not_setup: 'Account not fully set up. Please contact support.',
  },

  roles: {
    user: 'User',
    pending_office: 'Office (Pending Review)',
    office: 'Real Estate Office',
    admin: 'Administrator',
    subadmin: 'Moderator',
  },

  property: {
    property: 'Property',
    properties: 'Properties',
    title: 'Property Title',
    description: 'Description',
    price: 'Price',
    category: 'Category',
    listingType: 'Listing Type',
    sale: 'For Sale',
    rent: 'For Rent',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    area: 'Area',
    city: 'City',
    district: 'District',
    address: 'Address',
    amenities: 'Amenities',
    images: 'Images',
    status: 'Status',
    statuses: {
      draft: 'Draft',
      pending: 'Pending Review',
      active: 'Active',
      rejected: 'Rejected',
      hidden: 'Hidden',
      sold: 'Sold',
      rented: 'Rented',
      inactive: 'Inactive',
      archived: 'Archived',
    },
    categories: {
      residential: 'Residential',
      commercial: 'Commercial',
      land: 'Land',
      industrial: 'Industrial',
      farm_pool: 'Farm with Pool',
      tourism: 'Tourism',
    },
    addProperty: 'Add Property',
    editProperty: 'Edit Property',
    deleteProperty: 'Delete Property',
    viewDetails: 'View Details',
    contactOffice: 'Contact Office',
    whatsapp: 'WhatsApp',
    noProperties: 'No properties found',
    noPropertiesHint: 'Add your first property to start publishing',
    sqm: 'm²',
    currency: {
      SYP: 'SYP',
      USD: 'USD',
    },
    rooms: 'Rooms',
    floor: 'Floor',
    totalFloors: 'Total Floors',
    buildingAge: 'Building Age',
    furnished: 'Furnished',
    unfurnished: 'Unfurnished',
    propertyType: 'Property Type',
    direction: 'Direction',
    view: 'View',
    ownershipType: 'Ownership Type',
    paymentMethod: 'Payment Method',
    relatedProperties: 'Similar Properties',
    callNow: 'Call Now',
    emailContact: 'Email',
    features: 'Features',
    specifications: 'Specifications',
    years: 'years',
    kitchens: 'Kitchens',
    livingRooms: 'Living Rooms',
    linkCopied: 'Link copied to clipboard',
    shareProperty: 'Share',
    reference: 'Reference:',
    referenceCopy: 'Copy reference',
    referenceCopied: 'Reference copied',

    gallery: {
      prev: 'Previous image',
      next: 'Next image',
      goToImage: 'Go to image {n}',
      viewImage: 'View image {n}',
    },
    form: {
      title: 'Add new property',
      subtitle: 'Fill in the details to publish your listing',
      editTitle: 'Edit property',
      editSubtitle: 'Update your listing details',
      fixErrors: 'Please fix the fields highlighted in red before publishing',
    },
    section: {
      basic: 'Basic information',
      location: 'Location',
      details: 'Property details',
      features: 'Features',
      contact: 'Contact information',
      photos: 'Photos',
    },
    field: {
      title: 'Listing title',
      description: 'Description',
      propertyType: 'Property type',
      listingType: 'Listing type',
      price: 'Price',
      currency: 'Currency',
      governorate: 'Governorate',
      area: 'Area',
      address: 'Address',
      size: 'Size (m²)',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      livingRooms: 'Living rooms',
      kitchens: 'Kitchens',
      floor: 'Floor',
      totalFloors: 'Total floors',
      buildingAge: 'Building age (years)',
      direction: 'Direction',
      view: 'View',
      paymentMethod: 'Payment method',
      ownershipType: 'Ownership type',
      contactPhone: 'Contact phone',
      whatsapp: 'WhatsApp',
      videoUrl: 'Video URL (optional)',
    },
    types: {
      apartment: 'Apartment',
      villa: 'Villa',
      land: 'Land',
      shop: 'Shop',
      office: 'Office',
      building: 'Full building',
      farm_pool: 'Farm with Pool',
      tourism: 'Tourism / Tourist Property',
      other: 'Other',
    },
    feature: {
      parking: 'Parking',
      elevator: 'Elevator',
      balcony: 'Balcony',
      garden: 'Garden',
      pool: 'Pool',
      furnished: 'Furnished',
      ac: 'Air conditioning',
      heating: 'Heating',
      security: 'Security',
      storage: 'Storage room',
    },
    photos: {
      dropHint: 'Drag images here or click to select',
      coverBadge: 'Cover',
      setCover: 'Set as cover',
      fileTooLarge: 'Image too large (max 5MB)',
      invalidType: 'Unsupported file type',
      counter: '{n} / 15',
    },
    actions: {
      publish: 'Publish listing',
      cancel: 'Cancel',
      publishing: 'Publishing...',
      save: 'Save changes',
      saving: 'Saving...',
      hide: 'Hide',
      show: 'Show',
      viewPublic: 'View page',
      hiddenSuccess: 'Property hidden',
      shownSuccess: 'Property is now visible',
    },
    success: {
      created: 'Property published successfully',
      updated: 'Property updated successfully',
    },
    error: {
      createFailed: 'Could not publish property',
      updateFailed: 'Could not update property',
      notFound: 'Property not found',
    },
    warning: {
      partialImages: 'Property created, but some images failed to upload',
      partialImagesUpdate: 'Property updated, but some new images failed to upload',
    },
    directions: {
      north: 'North',
      south: 'South',
      east: 'East',
      west: 'West',
      northeast: 'Northeast',
      northwest: 'Northwest',
      southeast: 'Southeast',
      southwest: 'Southwest',
    },
    views: {
      street: 'Street',
      garden: 'Garden',
      sea: 'Sea',
      mountain: 'Mountain',
      city: 'City',
    },
    paymentMethods: {
      cash: 'Cash',
      installments: 'Installments',
      mixed: 'Cash & installments',
    },
    ownershipTypes: {
      freehold: 'Freehold',
      usufruct: 'Usufruct',
      waqf: 'Waqf',
    },
    farm: {
      pricing: 'Booking pricing',
      availability: 'Availability',
      dailyPrice: 'Price per night',
      weeklyPrice: 'Price per week',
      monthlyPrice: 'Price per month',
      minDays: 'Minimum days',
      maxDays: 'Maximum days',
      atLeastOnePrice: 'Set at least one price tier',
      maxLessThanMin: 'Maximum days must be greater than or equal to minimum days',
    },
  },

  bookings: {
    title: 'Bookings',
    subtitle: 'Manage farm booking requests',
    tabs: {
      all: 'All',
      pending: 'Pending',
      confirmed: 'Confirmed',
      past: 'Past',
    },
    status: {
      pending: 'Pending',
      confirmed: 'Confirmed',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
      completed: 'Completed',
    },
    list: {
      customer: 'Customer',
      dates: 'Dates',
      nights: '{n} nights',
      dayUse: 'Day use',
      note: 'Customer note',
      empty: 'No bookings',
    },
    actions: {
      confirm: 'Confirm booking',
      reject: 'Reject booking',
      details: 'Details',
    },
    confirm: {
      title: 'Confirm booking',
      noteLabel: 'Note to customer (optional)',
    },
    reject: {
      title: 'Reject booking',
      noteLabel: 'Reason for rejection (required)',
      notePlaceholder: 'At least 5 characters',
    },
    toast: {
      confirmed: 'Booking confirmed',
      rejected: 'Booking rejected',
      failed: 'Could not update booking',
    },
    customer: {
      requestTitle: 'Request a booking',
      perNight: 'per night',
      selectDates: 'Select your dates',
      nights: '{n} nights',
      total: 'Total',
      noteLabel: 'Note to the office (optional)',
      notePlaceholder: 'Any questions or requests?',
      request: 'Request booking',
      requestSent: 'Request sent',
      requestSentDesc: 'The office will review your request and confirm it shortly.',
      loginToBook: 'Log in to book',
      selectRange: 'Please select your check-in and check-out dates.',
      selectDate: 'Please select a date.',
      selectCheckout: 'Now select your check-out date',
      dayUse: 'Day use',
      dayUseHint: 'Single-day bookings available — tap a date',
      checkinAt: 'Check-in {time}',
      checkoutAt: 'Check-out {time}',
      arrivalDate: 'Arrival Date',
      arrivalTime: 'Arrival Time',
      departureDate: 'Departure Date',
      departureTime: 'Departure Time',
      arrival: 'Arrival',
      departure: 'Departure',
      summary: 'Booking summary',
      duration: '{n} hours',
      minHours: 'Minimum {n} hours.',
      invalidTimes: 'Departure must be after arrival.',
      minNights: 'Minimum stay is {n} nights.',
      maxNights: 'Maximum stay is {n} nights.',
      myBookings: 'My bookings',
      mySubtitle: 'Track and manage your booking requests',
      cancel: 'Cancel booking',
      cancelConfirm: 'Cancel this booking? This cannot be undone.',
      keepBooking: 'Keep booking',
      empty: "You haven't made any bookings yet.",
      toast: {
        requestSuccess: 'Booking request sent',
        cancelled: 'Booking cancelled',
        overlap: 'Those dates were just taken. Please pick another range.',
        failed: 'Could not complete your request',
      },
    },
  },

  calendar: {
    legend: {
      available: 'Available',
      booked: 'Booked',
    },
  },

  office: {
    office: 'Office',
    offices: 'Offices',
    officeName: 'Office Name',
    officeSlug: 'Office Slug',
    description: 'Description',
    city: 'City',
    phone: 'Phone',
    logo: 'Logo',
    companyDocument: 'Company Document',
    idDocument: 'ID Document',
    applyAsOffice: 'Apply as Real Estate Office',
    applyNow: 'Submit Application',
    applicationStatus: 'Application Status',
    applicationPending: 'Your application is under review',
    applicationApproved: 'Your application has been approved',
    applicationRejected: 'Your application has been rejected',
    applicationSubmitted: 'Your application has been submitted successfully',
    reapply: 'Submit new application',
    rejectionReason: 'Rejection Reason',
    slugHint: 'Will appear in your page URL, e.g.: /offices/my-office',
    whatsapp: 'WhatsApp',
    uploadLogo: 'Upload office logo',
    uploadDocument: 'Upload company document',
    uploadId: 'Upload ID document',
    dashboard: 'Office Dashboard',
    myProperties: 'My Properties',
    noOffice: 'No offices found',
    propertiesBy: 'Properties by',
    noOfficeProperties: 'This office has no active listings yet.',
    profile: {
      title: 'Office Profile',
      subtitle: 'Your business details and stats',
    },
    status: {
      approved: 'Approved',
      pending: 'Pending review',
      rejected: 'Rejected',
    },
    stats: {
      total: 'Total listings',
      active: 'Active',
      hidden: 'Hidden',
      sold: 'Sold/Rented',
    },
    meta: {
      registered: 'Registered on',
      approved: 'Approved on',
      officeId: 'Office ID',
    },
    form: {
      save: 'Save changes',
      saving: 'Saving...',
      saved: 'Changes saved',
    },
  },

  admin: {
    dashboard: 'Admin Dashboard',
    users: 'Users',
    offices: 'Offices',
    properties: 'Properties',
    applications: 'Office Applications',
    approve: 'Approve',
    reject: 'Reject',
    approved: 'Approved',
    rejected: 'Rejected',
    pending: 'Pending Review',
    confirmApprove: 'Are you sure you want to approve this application?',
    confirmReject: 'Are you sure you want to reject this application?',
    enterRejectionReason: 'Enter rejection reason',
    approveSuccess: 'Application approved successfully',
    rejectSuccess: 'Application rejected',
    viewDocuments: 'View Documents',
    applicantName: 'Applicant Name',
    submittedAt: 'Submitted At',
    reviewedAt: 'Reviewed At',
    allStatuses: 'All Statuses',
    pendingApplications: 'Pending Applications',
    rejectionReason: 'Rejection Reason',
    totalUsers: 'Total Users',
    totalOffices: 'Total Offices',
    totalProperties: 'Total Properties',
    pendingReview: 'Pending Review',
    deleteUser: 'Delete User',
    confirmDeleteUser: 'Are you sure you want to delete this user? This cannot be undone.',
    deleteUserSuccess: 'User deleted',
    demoteToUser: 'Change to User',
    confirmDemoteToUser: "Change this user's role to regular user?",
    demoteSuccess: 'Role updated successfully',
    documents: 'Documents',
    noDocuments: 'No documents uploaded',
    // User deactivation
    deactivateUser: 'Deactivate User',
    confirmDeactivateUser:
      'Deactivate this user? They will be hidden from the user list and cannot log in.',
    deactivateSuccess: 'User deactivated',
    reactivateUser: 'Reactivate User',
    reactivateSuccess: 'User reactivated',
    // Role management
    changeRole: 'Change Role',
    selectNewRole: 'Select new role',
    confirmChangeRole: 'Change role?',
    changeRoleSuccess: 'Role updated successfully',
    promoteToSubadmin: 'Make Moderator',
    promoteToOffice: 'Make Office',
    // Office management
    deleteOffice: 'Delete Office',
    confirmDeleteOffice: 'Delete this office and all its data? This cannot be undone.',
    deleteOfficeSuccess: 'Office deleted successfully',
    suspendOffice: 'Suspend Office',
    confirmSuspendOffice: 'Suspend this office? They will not be able to publish listings.',
    suspendSuccess: 'Office suspended',
    unsuspendOffice: 'Reactivate Office',
    unsuspendSuccess: 'Office reactivated',
    makeUnavailable: 'Mark Unavailable',
    makeAvailable: 'Mark Available',
    toggleAvailabilitySuccess: 'Availability updated',
    approveOffice: 'Approve Office',
    rejectOffice: 'Reject Office',
    confirmApproveOffice:
      'Approve this office? The owner will be notified and can start publishing.',
    confirmRejectOffice: 'Reject this office?',
    approveOfficeSuccess: 'Office approved',
    rejectOfficeSuccess: 'Office rejected',
    officeOwner: 'Owner',
    propertyCount: 'Listings',
    // Property management
    deleteProperty: 'Delete Property',
    confirmDeleteProperty: 'Delete this property permanently? This cannot be undone.',
    deletePropertySuccess: 'Property deleted successfully',
    unpublish: 'Unpublish',
    republish: 'Republish',
    unpublishSuccess: 'Property unpublished',
    republishSuccess: 'Property republished',
    propertyRef: 'Ref ID',
    ownerOffice: 'Office',
    actions: 'Actions',
    suspended: 'Suspended',
    // Office soft-hide / restore
    hideOffice: 'Hide Office',
    confirmHideOffice:
      'Hide this office? It will be removed from the public website. All data and properties are preserved.',
    hideOfficeSuccess: 'Office hidden',
    restoreOffice: 'Restore Office',
    confirmRestoreOffice: 'Restore this office to its previous status?',
    restoreOfficeSuccess: 'Office restored',
    hidden: 'Hidden',
    hiddenOffices: 'Hidden Offices',
    activeOffices: 'Active Offices',
    // Users tabs
    activeUsers: 'Active Users',
    deactivatedUsers: 'Deactivated Users',
    deactivationDate: 'Deactivated On',
    inquiries: 'Inquiries',
    // Property archive / restore
    archiveProperty: 'Archive Property',
    confirmArchiveProperty:
      'Archive this property? It will be removed from all listings. Images, favourites and inquiries are preserved.',
    archiveSuccess: 'Property archived',
    restoreProperty: 'Restore Property',
    confirmRestoreProperty: 'Restore this property to unpublished status?',
    restorePropertySuccess: 'Property restored',
    archived: 'Archived',
    archivedProperties: 'Archived Properties',
    activeProperties: 'Active Properties',
    publish: 'Publish',
    publishSuccess: 'Property published',
  },

  pages: {
    home: 'Home',
    search: 'Search Results',
    properties: 'All Properties',
    propertyDetail: 'Property Details',
    offices: 'Real Estate Offices',
    officeDetail: 'Office Details',
    contact: 'Contact Us',
    favorites: 'Favorites',
    savedProperties: 'Saved Properties',
    savedOffices: 'Saved Offices',
    noSavedProperties: 'No saved properties yet',
    noSavedOffices: 'No saved offices yet',
    latestProperties: 'Latest Properties',
    account: 'My Account',
    notFound: 'Page Not Found',
    notFoundDesc: 'Sorry, the page you are looking for does not exist.',
    goHome: 'Go Home',
  },

  search: {
    placeholder: 'Search for a property...',
    results: 'Search Results',
    noResults: 'No properties match your search',
    filters: 'Filter Options',
    advancedSearch: 'Advanced Search',
    priceRange: 'Price Range',
    minPrice: 'Min Price',
    maxPrice: 'Max Price',
    priceFrom: 'Price From',
    priceTo: 'Price To',
    areaFrom: 'Area From',
    areaTo: 'Area To',
    anyCity: 'Any City',
    anyCategory: 'Any Category',
    anyType: 'All',
    anyGovernorate: 'Governorate',
    anyArea: 'Area',
    propertyType: 'Property Type',
    furnished: 'Furnished',
    furnishedYes: 'Furnished',
    furnishedNo: 'Unfurnished',
    floor: 'Floor',
    rooms: 'Rooms',
    bathrooms: 'Bathrooms',
    heroTitle: 'Find Your Perfect Property in Syria',
    heroSubtitle: 'Thousands of properties for sale and rent across Syria',
  },

  home: {
    featured: {
      title: 'Featured Properties',
      subtitle: 'Browse our latest listings',
      view_all: 'View all properties',
    },
    referenceSearch: {
      label: 'Find by reference ID',
      placeholder: 'e.g. SY14-00123',
      submit: 'Find',
      invalid: 'Invalid reference format',
      notFound: 'No property found with this reference',
    },
  },

  blog: {
    sectionTitle: 'From Our Blog',
    sectionSubtitle: 'Articles on Syrian culture, history, and real estate investment',
    readMore: 'Read More',
    viewAll: 'View All Articles',
    allArticles: 'All Articles',
    minRead: 'read',
    author: 'Syria 14 Team',
    shareArticle: 'Share Article',
    relatedArticles: 'Related Articles',
    tableOfContents: 'Table of Contents',
    backToBlog: 'Back to Blog',
    notFound: 'Article not found',
    pageTitle: 'Blog',
    pageSubtitle: 'Latest articles on culture, history, and real estate in Syria',
  },

  errors: {
    generic: 'An unexpected error occurred. Please try again.',
    notFound: 'Item not found.',
    unauthorized: 'You are not authorized to access this page.',
    forbidden: 'Access forbidden.',
    network: 'Network connection error.',
  },

  account: {
    title: 'Account',
    info: {
      heading: 'Account information',
      accountType: 'Account type',
      realEstateOffice: 'Real Estate Office',
      joined: 'Joined',
    },
    actions: {
      editProfile: 'Edit profile',
    },
    security: {
      heading: 'Security',
    },
    notifications: {
      heading: 'Email notifications',
      inquiries: 'New property inquiries',
      system: 'System announcements',
      saved: 'Saved',
    },
  },

  notifications: {
    title: 'Notifications',
    empty: 'No notifications yet',
    markAllRead: 'Mark all as read',
    viewAll: 'View all',
    justNow: 'just now',
  },

  validation: {
    required: 'This field is required',
    tooShort: 'Too short',
    tooLong: 'Too long',
    notANumber: 'Must be a number',
    mustBePositive: 'Must be a positive number',
    invalidUrl: 'Invalid URL',
    invalidPhone: 'Invalid phone number',
    atLeastOneImage: 'At least one image is required',
    invalidEmail: 'Invalid email address',
    atLeastOne: 'Select at least one option',
  },

  register: {
    sectionAccount: 'Account details',
    sectionOffice: 'Office details',
    sectionDocuments: 'Official documents',
    documentsHint: 'Required for review and approval',
    companyDocLabel: 'Commercial register or official company document',
    idDocLabel: 'Personal identity document',
    submitOffice: 'Register and submit office application',
    chooseType: 'Choose account type to continue',
    userTitle: 'Regular user',
    userDescription: 'For individuals looking to search for properties and save favourites',
    officeTitle: 'Real estate office',
    officeDescription: 'For offices wishing to publish their properties and manage listings',
    userFormTitle: 'Create user account',
    officeFormTitle: 'Register a real estate office',
    selectCity: 'Select city',
    invalidDocType: 'Please upload an image or PDF file',
    docTooLarge: 'Maximum file size is {max}MB',
    docHint: 'JPG, PNG, PDF — max {max}MB',
    cityRequired: 'Please select a city',
    companyDocRequired: 'Please upload the official company document',
    idDocRequired: 'Please upload your identity document',
    officeSuccess: {
      title: 'Application submitted successfully',
      body: 'Your office application is under review. You will be notified once it is approved.',
      cta: 'Go to home page',
    },
  },

  contact: {
    subtitle: 'We are happy to hear from you and answer your inquiries',
    form: {
      name: 'Full name',
      contact: 'Email or phone number',
      message: 'Your message',
      messagePlaceholder: 'Write your message here...',
      send: 'Send message',
      sending: 'Sending...',
      successTitle: 'Message sent successfully',
      successBody: 'We will get back to you as soon as possible.',
      sendAnother: 'Send another message',
    },
    info: {
      heading: 'Contact information',
      phone: '+963 992 957 996',
      whatsapp: '+963 992 957 996',
      email: 'info@syria14.com',
      address: 'Damascus, Syria',
      hours: 'Sunday – Thursday, 9 AM – 5 PM',
      phoneLabel: 'Phone',
      whatsappLabel: 'WhatsApp',
      emailLabel: 'Email',
      addressLabel: 'Address',
      hoursLabel: 'Working hours',
    },
  },
} as const;
